import { Controller } from "@hotwired/stimulus"

const SUMMARY_NAME_LIMIT = 3

// Edits affect the posted fields directly; closing without Apply must restore them.
export default class extends Controller {
  static targets = [
    "applyButton",
    "checkbox",
    "clearShownButton",
    "count",
    "detail",
    "emptyHint",
    "headline",
    "openButton",
    "resetButton",
    "search",
    "selectShownButton",
  ]
  static values = { total: Number }

  apply() {
    this.applied = true
  }

  begin() {
    this.applied = false
    this.snapshot = this.checkboxTargets.map((checkbox) => checkbox.checked)
    this.searchTarget.value = ""
    this.searchTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.refresh()
  }

  clearShown() {
    this.shownCheckboxes.forEach((checkbox) => (checkbox.checked = false))
    this.refresh()
  }

  closed = () => {
    if (!this.applied && this.snapshot) {
      this.checkboxTargets.forEach((checkbox, i) => (checkbox.checked = this.snapshot[i]))
    }
    this.snapshot = null
    this.render()
    this.notifyForm()
  }

  connect() {
    this.dialog = this.element.querySelector("dialog")
    this.dialog?.addEventListener("close", this.closed)
    this.render()
  }

  disconnect() {
    this.dialog?.removeEventListener("close", this.closed)
  }

  // Enter in the search box would otherwise submit the report form behind the dialog.
  ignoreEnter(event) {
    event.preventDefault()
  }

  // Programmatic checkbox changes do not emit change events.
  notifyForm() {
    this.element.dispatchEvent(new Event("change", { bubbles: true }))
  }

  refresh() {
    const selected = this.selectedCheckboxes.length
    const filtering = this.searchTarget.value.trim() !== ""
    const shown = this.shownCheckboxes.length

    this.countTarget.textContent = `${selected} of ${this.totalValue} selected`
    this.applyButtonTarget.disabled = selected === 0
    this.emptyHintTarget.classList.toggle("hidden", selected > 0)
    this.selectShownButtonTarget.textContent = filtering ? `Select ${shown} shown` : "Select all"
    this.clearShownButtonTarget.textContent = filtering ? "Clear shown" : "Clear"
    this.selectShownButtonTarget.disabled = shown === 0
    this.clearShownButtonTarget.disabled = shown === 0
  }

  render() {
    const selected = this.selectedCheckboxes
    const all = selected.length === this.totalValue

    if (all) {
      this.headlineTarget.textContent = "All companies"
      this.detailTarget.textContent = `Every company you may run this report for (${this.totalValue})`
    } else {
      const names = selected.slice(0, SUMMARY_NAME_LIMIT).map((checkbox) => checkbox.dataset.companyPickerName)
      const more = selected.length - names.length
      if (more > 0) names.push(`+${more} more`)

      this.headlineTarget.textContent = `${selected.length} of ${this.totalValue} companies`
      this.detailTarget.textContent = names.join(", ")
    }

    this.openButtonTarget.querySelector("span")?.replaceChildren(
      all ? "Choose specific companies" : "Change"
    )
    this.resetButtonTarget.classList.toggle("hidden", all)
    this.refresh()
  }

  selectEverything() {
    this.checkboxTargets.forEach((checkbox) => (checkbox.checked = true))
    this.render()
    this.notifyForm()
  }

  selectShown() {
    this.shownCheckboxes.forEach((checkbox) => (checkbox.checked = true))
    this.refresh()
  }

  get selectedCheckboxes() {
    return this.checkboxTargets.filter((checkbox) => checkbox.checked)
  }

  get shownCheckboxes() {
    return this.checkboxTargets.filter((checkbox) => !checkbox.closest("label").classList.contains("hidden"))
  }
}
